import { useState, useEffect } from 'react';
import { useToast } from '../hooks/useToast.tsx';
import type { HistoricoMesa } from '../types';
import { historicoMesasApi } from '../services/api';
import { Calendar } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { pt } from 'date-fns/locale';

export function HistoricoMesasPage() {
  const { error } = useToast();
  const [historico, setHistorico] = useState<HistoricoMesa[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterData, setFilterData] = useState<string>('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const response = await historicoMesasApi.getAll(filterData || undefined, 1, 100);
        if (active && response.success && response.data) {
          setHistorico(response.data.items);
        }
      } catch {
        if (active) error('Erro ao carregar histórico');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [filterData, error]);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Histórico de Mesas</h1>
        <div className="header-actions">
          <div className="filter-group">
            <Calendar className="w-4 h-4" />
            <input
              type="date"
              className="input input-sm"
              value={filterData}
              onChange={e => setFilterData(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Data/Hora</th>
                <th>Mesa</th>
                <th>Origem</th>
                <th>Cliente</th>
                <th>Pessoas</th>
                <th>Valor</th>
                <th>Pago</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {historico.map(h => (
                <tr key={h.id}>
                  <td>{format(parseISO(h.dataMesa), 'dd/MM/yyyy HH:mm', { locale: pt })}</td>
                  <td>{h.mesa ? `Mesa ${h.mesa.numero}` : '—'}</td>
                  <td>
                    <span className={`badge ${h.origem === 10 ? 'badge-info' : h.origem === 1 ? 'badge-warning' : 'badge-neutral'}`}>
                      {h.origem === 10 ? 'Reserva' : h.origem === 1 ? 'Ocupação' : 'Normal'}
                    </span>
                  </td>
                  <td>{h.cliente?.nome || h.contatoReserva || '—'}</td>
                  <td>{h.numeroPessoas}</td>
                  <td>{h.valor.toFixed(2)} €</td>
                  <td>
                    <span className={`badge ${h.pago ? 'badge-success' : 'badge-warning'}`}>
                      {h.pago ? 'Sim' : 'Não'}
                    </span>
                  </td>
                  <td>
                    {h.encerrado ? (
                      <span className="badge badge-neutral">
                        Saída{h.dataSaida ? ` ${format(parseISO(h.dataSaida), 'HH:mm', { locale: pt })}` : ''}
                      </span>
                    ) : (
                      <span className="badge badge-warning">Em curso</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading && <div style={{ padding: 16, color: 'var(--c-text-muted)', fontSize: 13 }}>A carregar histórico...</div>}
        {!loading && historico.length === 0 && (
          <div className="empty-state">
            <Calendar className="w-12 h-12 text-gray-300" />
            <h3>Nenhum registo no histórico</h3>
          </div>
        )}
      </div>
    </div>
  );
}